import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  Button,
  Text,
  Heading,
  Badge,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  Alert,
  AlertIcon,
  HStack,
  Tooltip,
  IconButton
} from '@chakra-ui/react';
import { 
  ChevronRightIcon, 
  AddIcon, 
  EditIcon,
  ViewIcon,
  ExternalLinkIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FiEdit, FiEye, FiExternalLink, FiLayout, FiCopy, FiGrid, FiPackage } from 'react-icons/fi';
import apiClient from '../../../utils/apiClient';
import DataTable from '../../common/DataTable/DataTable';

const Pages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [components, setComponents] = useState([]);
  const [layouts, setLayouts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchPages();
      fetchSites();
      fetchTemplates();
      fetchComponents();
      fetchLayouts();
    }
  }, [tenantId]);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const response = await apiClient(`/pages/${tenantId}`); // Add /api prefix
      
      console.log('Pages response:', response); // Debug log
      
      let pagesData = [];
      if (response && response.success && Array.isArray(response.data)) {
        // Correct: Use response.data directly since it's already an array
        pagesData = response.data;
      } else if (Array.isArray(response)) {
        // Fallback: Direct array response
        pagesData = response;
      } else {
        console.warn('Unexpected response format:', response);
        pagesData = [];
      }
      
      console.log('Processed pages data:', pagesData); // Debug log
      setPages(pagesData);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast({
        title: 'Error loading pages',
        description: error.message || 'Failed to load pages',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSites = async () => {
    try {
      const response = await apiClient(`/sites/${tenantId}`);
      let sitesData = [];
      
      if (response.success) {
        sitesData = Object.keys(response)
          .filter(key => key !== 'success')
          .map(key => response[key]);
      } else if (Array.isArray(response)) {
        sitesData = response;
      } else if (response.data) {
        sitesData = Array.isArray(response.data) ? response.data : [];
      }
      
      setSites(sitesData);
    } catch (error) {
      console.error('Error fetching sites:', error);
      setSites([]);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await apiClient(`/templates/${tenantId}`);
      let templatesData = [];
      
      if (response.success) {
        templatesData = Object.keys(response)
          .filter(key => key !== 'success')
          .map(key => response[key]);
      } else if (Array.isArray(response)) {
        templatesData = response;
      } else if (response.data) {
        templatesData = Array.isArray(response.data) ? response.data : [];
      }
      
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setTemplates([]);
    }
  };

  const fetchComponents = async () => {
    try {
      const response = await apiClient(`/page-components/${tenantId}`);
      let componentsData = [];
      
      if (response.success) {
        componentsData = Object.keys(response)
          .filter(key => key !== 'success')
          .map(key => response[key]);
      } else if (Array.isArray(response)) {
        componentsData = response;
      } else if (response.data) {
        componentsData = Array.isArray(response.data) ? response.data : [];
      }
      
      setComponents(componentsData);
    } catch (error) {
      console.error('Error fetching components:', error);
      setComponents([]);
    }
  };

  const fetchLayouts = async () => {
    try {
      const response = await apiClient(`/component-layouts/${tenantId}`);
      let layoutsData = [];
      
      if (response.success) {
        layoutsData = Object.keys(response)
          .filter(key => key !== 'success')
          .map(key => response[key]);
      } else if (Array.isArray(response)) {
        layoutsData = response;
      } else if (response.data) {
        layoutsData = Array.isArray(response.data) ? response.data : [];
      }
      
      setLayouts(layoutsData);
    } catch (error) {
      console.error('Error fetching layouts:', error);
      setLayouts([]);
    }
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleCreate = () => {
    if (sites.length === 0) {
      toast({
        title: 'No sites available',
        description: 'You need to create a site first before you can create pages.',
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
    navigate('/pages/create');
  };

  const handleEdit = (page) => {
    navigate(`/pages/edit/${page.id}`);
  };

  const handleDelete = async (page) => {
    try {
      const response = await apiClient(`/pages/${tenantId}/${page.id}`, {
        method: 'DELETE'
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Page deleted successfully',
        description: `"${page.name}" has been deleted`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast({
        title: 'Error deleting page',
        description: error.message || 'Failed to delete page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const publishPage = async (page) => {
    try {
      const response = await apiClient(`/pages/${tenantId}/${page.id}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Page published successfully',
        description: `"${page.name}" is now live`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchPages();
    } catch (error) {
      console.error('Error publishing page:', error);
      toast({
        title: 'Error publishing page',
        description: error.message || 'Failed to publish page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const duplicatePage = async (page) => {
    try {
      const response = await apiClient(`/pages/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...page,
          name: `${page.name} (Copy)`,
          path: `${page.path}-copy`,
          status: 'draft',
          is_home: false
        })
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Page duplicated successfully',
        description: `"${page.name}" has been copied`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      fetchPages();
    } catch (error) {
      console.error('Error duplicating page:', error);
      toast({
        title: 'Error duplicating page',
        description: error.message || 'Failed to duplicate page',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'yellow';
      case 'archived': return 'gray';
      default: return 'gray';
    }
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id?.toString() === siteId?.toString());
    return site ? site.name : 'Unknown Site';
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id?.toString() === templateId?.toString());
    return template ? template.name : 'Unknown Template';
  };

  const getComponentCount = (pageId) => {
    // This would come from page_component_instances table
    // For now, return a random number for demo
    return Math.floor(Math.random() * 8) + 1;
  };

  // Filter data based on search term
  const filteredPages = pages.filter(page => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      page.name?.toLowerCase().includes(searchLower) ||
      page.path?.toLowerCase().includes(searchLower) ||
      page.description?.toLowerCase().includes(searchLower) ||
      page.site_name?.toLowerCase().includes(searchLower)
    );
  });

  // Define columns for DataTable
  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, page) => (
        <VStack align="start" spacing={1}>
          <HStack>
            <Text fontWeight="medium">{page.name}</Text>
            {page.is_home && (
              <Badge colorScheme="purple" size="sm">
                Homepage
              </Badge>
            )}
          </HStack>
          {page.description && (
            <Text fontSize="sm" color="gray.500" noOfLines={1}>
              {page.description}
            </Text>
          )}
          <HStack spacing={2}>
            <Badge colorScheme="blue" size="sm" variant="outline">
              {getComponentCount(page.id)} components
            </Badge>
            {page.layout_id && (
              <Badge colorScheme="green" size="sm" variant="outline">
                Custom Layout
              </Badge>
            )}
          </HStack>
        </VStack>
      )
    },
    {
      key: 'path',
      label: 'Path',
      sortable: true,
      render: (value) => (
        <Text fontFamily="mono" fontSize="sm" color="blue.500">
          {value}
        </Text>
      )
    },
    {
      key: 'site_name',
      label: 'Site',
      sortable: true,
      render: (value, page) => (
        <Badge colorScheme="blue" variant="subtle">
          {page.site_name || getSiteName(page.site_id)}
        </Badge>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => (
        <Badge colorScheme={getStatusColor(value)}>
          {value}
        </Badge>
      )
    },
    // {
    //   key: 'template_name',
    //   label: 'Template',
    //   sortable: true,
    //   render: (value, page) => (
    //     <Text fontSize="sm">
    //       {page.template_name || getTemplateName(page.template_id)}
    //     </Text>
    //   )
    // },
    {
      key: 'updated_at',
      label: 'Last Modified',
      sortable: true,
      render: (value) => (
        <Text fontSize="sm">
          {new Date(value).toLocaleDateString()}
        </Text>
      )
    }
  ];

  // Filter configuration for DataTable
  const filterConfig = {
    status: {
      label: 'Status',
      options: [
        { id: 'draft', name: 'Draft' },
        { id: 'published', name: 'Published' },
        { id: 'archived', name: 'Archived' }
      ],
      colorScheme: 'green'
    },
    site_id: {
      label: 'Site',
      options: sites.map(site => ({
        id: site.id,
        name: site.name
      })),
      colorScheme: 'blue'
    }
  };

  // Custom actions for DataTable
  const customActions = [
    {
      label: 'Open Page Builder',
      icon: <FiLayout />,
      onClick: (page) => navigate(`/pages/${page.id}/builder`, {
        state: { 
          pageData: page,
          availableComponents: components,
          availableLayouts: layouts,
          sites,
          templates
        }
      }),
      colorScheme: 'purple'
    },
    {
      label: 'Duplicate Page',
      icon: <FiCopy />,
      onClick: duplicatePage,
      colorScheme: 'gray'
    },
    {
      label: 'Preview',
      icon: <FiEye />,
      onClick: (page) => {
        const previewUrl = `${window.location.origin}/dashboard/pages/${page.id}/preview`;
        window.open(previewUrl, '_blank');
      },
      colorScheme: 'blue'
    },
    {
      label: 'Publish',
      icon: <FiExternalLink />,
      onClick: publishPage,
      colorScheme: 'green',
      show: (page) => page.status === 'draft'
    }
  ];

  if (!tenantId) {
    return (
      <Container maxW="container.2xl" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.2xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Pages</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {sites.length === 0 && !loading && (
          <Alert status="info">
            <AlertIcon />
            You need to create a site first before you can create pages.{' '}
            <Button
              as={RouterLink}
              to="/sites/create"
              variant="link"
              colorScheme="blue"
              ml={2}
            >
              Create Site
            </Button>
          </Alert>
        )}

        <HStack justify="space-between">
          <Heading size="lg">Pages</Heading>
          <HStack spacing={3}>
            <Button
              leftIcon={<FiPackage />}
              variant="outline"
              onClick={() => navigate('/component-library')}
            >
              Component Library
            </Button>
            <Button
              leftIcon={<FiGrid />}
              variant="outline"
              onClick={() => navigate('/layout-library')}
            >
              Layout Library
            </Button>
          </HStack>
        </HStack>

        <DataTable
          title="Website Pages"
          columns={columns}
          data={filteredPages}
          onSearch={handleSearch}
          onEdit={handleEdit}
          onCreate={handleCreate}
          onDelete={handleDelete}
          onRefresh={fetchPages}
          searchPlaceholder="Search pages by name, path, or description..."
          isLoading={loading}
          createButtonText="Create Page"
          emptyStateTitle="No pages found"
          emptyStateDescription={
            pages.length === 0 
              ? "Get started by creating your first page"
              : "No pages match your current search criteria"
          }
          loadingText="Loading pages..."
          filterConfig={filterConfig}
          customActions={customActions.filter(action => 
            !action.show || filteredPages.some(page => action.show(page))
          )}
        />
      </VStack>
    </Container>
  );
};

export default Pages;